import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  EnvelopeIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

import AuthLeftPanel from '../components/AuthLeftPanel';
import PasswordVisibilityToggle from '../components/common/PasswordVisibilityToggle';
import { useAuth } from '../hooks/useAuth';
import { UserRole, isAdminUser } from '../types/auth';
import { getLoginErrorMessage } from '../utils/apiErrors';
import { Input } from '../design-system/components/Input';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { PatternOverlay } from '../design-system/components/Decorative';
import { Stack } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { shadows } from '../design-system/tokens/shadows';
import { hexToRgba } from '../utils/colorUtils';
import { gradientPatterns } from '../design-system/tokens/gradients';

const LoginCard = styled(Card)`
  padding: ${spacing[8]};
`;

const CardTitle = styled(Heading)`
  margin-bottom: ${spacing[2]};
`;

const LoginContainer = styled.div`
  display: flex;
  height: calc(100vh - 73px);
  min-height: 0;
  max-height: calc(100vh - 73px);
  position: relative;
  background: ${props => props.theme.colors.gradientBackground};
  overflow: hidden;

  ${breakpoints.down.md} {
    flex-direction: column;
    height: auto;
    max-height: none;
    min-height: calc(100vh - 73px);
    overflow: visible;
  }
`;

const HeroPanel = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  min-height: 0;
  max-width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: ${spacing[8]} ${spacing[6]};
  background: ${props => gradientPatterns.subtleBackground(props.theme.colors)};
  overflow: hidden;
  
  ${breakpoints.down.md} {
    display: none;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.theme.colors.gradientPrimary};
    opacity: 0.05;
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.gradientPrimary};
    z-index: 1;
    
    ${breakpoints.down.md} {
      display: none;
    }
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 100%;
  z-index: 2;
  position: relative;
  padding: ${spacing[6]};

  ${breakpoints.up.md} {
    padding: ${spacing[8]};
  }
`;

const FormPanel = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  min-height: 0;
  max-width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[6]};
  background: ${props => props.theme.colors.surface};
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  
  ${breakpoints.down.md} {
    max-width: 100%;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.theme.colors.gradientPrimary};
    z-index: 0;

    ${breakpoints.down.md} {
      width: 100%;
      height: 4px;
      top: 0;
      left: 0;
    }
  }
`;

const FormCardWrapper = styled.div`
  width: 100%;
  max-width: 450px;
  z-index: 1;
  position: relative;
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: ${spacing[6]};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const MessageBlock = styled.div`
  margin-bottom: ${spacing[6]};
`;

const FooterLinks = styled.div`
  margin-top: ${spacing[6]};
  text-align: center;
  padding-top: ${spacing[6]};
  border-top: 1px solid ${props => props.theme.colors.borderLight};
`;

const SubmitButtonWrap = styled.div`
  margin-top: ${spacing[2]};
`;

const ErrorMessage = styled(Card)`
  background: ${props => {
    const errorRgba = hexToRgba(props.theme.colors.error, 0.1);
    return `linear-gradient(135deg, ${errorRgba} 0%, ${hexToRgba(props.theme.colors.error, 0.08)} 100%)`;
  }};
  color: ${props => props.theme.colors.error};
  padding: ${spacing[4]} ${spacing[5]};
  border: 1px solid ${props => hexToRgba(props.theme.colors.error, 0.3)};
  margin-bottom: ${spacing[4]};
  box-shadow: ${props => shadows.colored.md(props.theme.colors.error)};
  border-left: 4px solid ${props => props.theme.colors.error};
`;

const StyledLink = styled(Link)`
  color: ${props => props.theme.colors.icomBlue};
  text-decoration: none;
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.sm};
  position: relative;
  transition: color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors.icomBlue};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    
    &::after {
      width: 100%;
    }
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.icomBlue};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const ForgotPasswordRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: -${spacing[1]};
  margin-bottom: ${spacing[1]};
`;

const WarningMessage = styled(Card)`
  background: ${props => {
    const warningRgba = hexToRgba(props.theme.colors.warning, 0.1);
    return `linear-gradient(135deg, ${warningRgba} 0%, ${hexToRgba(props.theme.colors.warning, 0.08)} 100%)`;
  }};
  color: ${props => props.theme.colors.warning};
  padding: ${spacing[4]} ${spacing[5]};
  border: 1px solid ${props => hexToRgba(props.theme.colors.warning, 0.3)};
  margin-bottom: ${spacing[4]};
  box-shadow: ${props => shadows.colored.md(props.theme.colors.warning)};
  border-left: 4px solid ${props => props.theme.colors.warning};
`;

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    setIsLoading(true);

    try {
      const user = await login(formData);
      const isAdmin = isAdminUser(user);
      if (isAdmin) {
        navigate('/admin');
      } else if (user?.role === UserRole.InstitutionMember && user?.profileCompleted === false) {
        navigate('/profile?edit=1');
      } else if (user?.role === UserRole.InstitutionMember) {
        navigate('/institution');
      } else if (user?.role === UserRole.IndividualMember && user?.profileCompleted === false) {
        // Existing-member first-time login: send to profile/edit to complete their profile
        navigate('/profile/edit');
      } else {
        navigate('/');
      }
    } catch (err: unknown) {
      const rawMessage = err instanceof Error ? err.message : '';
      const message = getLoginErrorMessage(rawMessage, t);
      setError(message);
      if (typeof rawMessage === 'string' && rawMessage.toLowerCase().includes('confirm')) {
        setShowResend(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!formData.email.trim()) return;
    setResending(true);
    try {
      const { authService } = await import('../services/auth');
      await authService.resendConfirmationEmail(formData.email.trim());
      setError(t('register.confirmationEmailResent'));
      setShowResend(false);
    } catch (e: any) {
      setError(
        e?.message ||
          (t('register.resendFailed'))
      );
    } finally {
      setResending(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <LoginContainer>
      <HeroPanel>
        <PatternOverlay pattern="organic" intensity="subtle" opacity={0.3}>
          <HeroContent>
            <AuthLeftPanel />
          </HeroContent>
        </PatternOverlay>
      </HeroPanel>

      <FormPanel>
        <FormCardWrapper>
          <LoginCard
            variant="elevated" 
            headerVariant="gradient"
            accentBorder="top"
            pattern="dots"
            patternIntensity="subtle"
          >
            <FormHeader>
              <CardTitle variant="h2" color="text">
                {t('login.signIn')}
              </CardTitle>
              <Text color="secondary" size="sm">
                {t('login.signInSubtitle')}
              </Text>
            </FormHeader>

            {error && (
              <MessageBlock>
                <ErrorMessage variant="filled">
                  <Text color="error" size="sm" weight="medium">{error}</Text>
                </ErrorMessage>
              </MessageBlock>
            )}

            {showResend && (
              <MessageBlock>
                <WarningMessage variant="filled">
                <Stack direction="column" spacing="sm">
                  <Text color="warning" size="sm" weight="semibold">
                    {t('login.emailNotConfirmed')}
                  </Text>
                  <Text color="warning" size="xs">
                    {t('login.emailNotConfirmedMessage')}
                  </Text>
                  <Button
                    type="button"
                    variant="outline"
                    loading={resending}
                    onClick={handleResend}
                    disabled={!formData.email.trim()}
                  >
                    {t('login.resendConfirmation')}
                  </Button>
                </Stack>
                </WarningMessage>
              </MessageBlock>
            )}

            <Form onSubmit={handleSubmit}>
              <Stack direction="column" spacing="md">
                <Input
                  id="login-email"
                  type="text"
                  name="email"
                  autoComplete="username"
                  label={t('login.emailOrUsername') || t('login.email')}
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('login.emailOrUsernamePlaceholder') || t('login.emailPlaceholder')}
                  variant="outlined"
                  iconLeft={<EnvelopeIcon />}
                  required
                />

                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  label={t('login.password')}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('login.passwordPlaceholder')}
                  variant="outlined"
                  iconLeft={<LockClosedIcon />}
                  iconRight={
                    <PasswordVisibilityToggle
                      visible={showPassword}
                      onToggle={togglePasswordVisibility}
                      controlsId="login-password"
                    />
                  }
                  required
                />

                <ForgotPasswordRow>
                  <StyledLink to="/forgot-password">{t('login.forgot')}</StyledLink>
                </ForgotPasswordRow>

                <SubmitButtonWrap>
                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                    glow
                    ripple
                  >
                    {t('login.submit')}
                  </Button>
                </SubmitButtonWrap>
              </Stack>
            </Form>

            <FooterLinks>
              <Text size="sm" color="secondary">
                {t('login.noAccount')}{' '}
                <StyledLink to="/register">{t('login.register')}</StyledLink>
              </Text>
            </FooterLinks>
          </LoginCard>
        </FormCardWrapper>
      </FormPanel>
    </LoginContainer>
  );
};

export default Login;
